import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { API_BASE } from "@/services/api";
import { Loader2, AlertCircle } from "lucide-react";

/**
 * Handles the return trip from Emergent-managed Google OAuth.
 * Emergent redirects back to this route with `#session_id=<id>` in the URL
 * fragment. We exchange it server-side for our app JWT, then continue.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("No sign-in session was found. Please try again.");
      setTimeout(() => navigate("/auth"), 2000);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/auth/google/session`, {
          session_id: sessionId,
        });
        // Persist using the same shape the rest of the app expects.
        localStorage.setItem("token", data.token.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setAuth({ user: data.user, token: data.token.access_token });
        // Clear the fragment so the session_id isn't re-processed.
        window.history.replaceState(null, "", window.location.pathname);
        // Returning users skip the one-time exam selector → straight to dashboard.
        // New users still see the selector so they can pick their exam.
        if (!data.is_new_user) {
          const uid = data.user.id;
          if (!localStorage.getItem(`clarias_exam_${uid}`)) {
            localStorage.setItem(`clarias_exam_${uid}`, "UPSC");
          }
          localStorage.setItem(`clarias_exam_selector_v2_${uid}`, "true");
        }
        navigate("/");
      } catch (err) {
        const msg = err?.response?.data?.detail || "Google sign-in failed. Please try again.";
        setError(typeof msg === "string" ? msg : "Google sign-in failed. Please try again.");
        setTimeout(() => navigate("/auth"), 2500);
      }
    })();
  }, [navigate, setAuth]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220] flex items-center justify-center px-4"
      data-testid="google-auth-callback"
    >
      <div className="text-center space-y-4">
        {error ? (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
            <p className="text-slate-200 max-w-sm" data-testid="google-auth-error">{error}</p>
            <p className="text-slate-500 text-sm">Redirecting you back to sign-in…</p>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-orange-400 mx-auto animate-spin" />
            <p className="text-slate-200">Signing you in with Google…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
